import { BookingTypePresenter } from '@shared/modules/booking-types/booking-type-presenter';
import type { Carer } from '@shared/modules/carers/carer';
import { CarerPresenter } from '@shared/modules/carers/carer.presenter';
import { ClientPresenter } from '@shared/modules/clients/client.presenter';
import { ScheduleEntryPresenter } from '@shared/modules/schedule/schedule-entry.presenter';
import type { Team } from '@shared/modules/teams/team';
import { TeamPresenter } from '@shared/modules/teams/team-presenter';
import { niceList } from '@shared/utils/strings';
import { formatWarnings } from '@shared/utils/warnings';
import type { ClientScheduleEntry } from './client-schedule-entry';
import { AddressPresenter } from '@shared/modules/addresses/address.presenter';

export class ClientScheduleEntryPresenter extends ScheduleEntryPresenter<ClientScheduleEntry> {
    get optionLabel(): string {
        return this.title;
    }

    get title() {
        return this._get('title', () => `${this.bookingTypeReadable} • ${this.startTime} (${this.duration})`);
    }

    get teams(): Team[] {
        return this.present('teams.data', TeamPresenter);
    }

    get teamNames() {
        return this._get('teamNames', () => niceList((this.teams || []).map(team => team.description)));
    }

    get bookingType(): BookingTypePresenter {
        return this.present('booking_type.data', BookingTypePresenter);
    }

    get bookingTypeReadable() {
        return this._get('bookingTypeReadable', () => {
            const bookingTypePresenter = this.bookingType;

            return bookingTypePresenter ? bookingTypePresenter.description : 'Unspecified';
        });
    }

    get client(): ClientPresenter {
        return this.present('client.data', ClientPresenter);
    }

    get carers(): CarerPresenter[] {
        return this.presentCollection<Carer, CarerPresenter>('carers.data', CarerPresenter).sort((a, b) => a.name > b.name ? 1 : 0);
    }

    get address(): AddressPresenter {
        return this.present('address.data', AddressPresenter);
    }

    /**
     * The names of the carers assigned to this booking. An array is returning as it is
     * more flexible for formatting.
     *
     * @return
     */
    get carerNames(): string[] {
        return this._get('carerNames', () => this.carers.map(carer => carer.person.nameNormal).sort((a, b) => (a > b ? 1 : -1)));
    }

    get warnings() {
        return this._get('warnings', () => formatWarnings(this.get('warnings.data', [])));
    }
}
